import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Eye, EyeOff, Copy, Loader2, Package, Wifi, Crown, Briefcase, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visa Logo Component
const VisaLogo = ({ className = "w-16 h-auto" }) => (
  <svg className={className} viewBox="0 0 1000 324" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M651.185 0.5C661.185 0.5 671.685 4 680.685 7.5L667.685 69C660.185 65.5 649.185 61 637.185 61C608.685 61 605.185 74.5 605.185 82C605.185 97.5 620.685 106 641.185 120.5C671.685 141 686.685 164.5 686.685 196C686.685 264 631.185 299.5 563.685 299.5C536.685 299.5 510.685 293 493.185 285L507.185 221C522.185 229 542.685 238.5 567.185 238.5C592.685 238.5 612.185 226 612.185 205C612.185 187.5 598.685 175.5 572.185 159C546.185 143 517.685 117.5 517.685 80C517.685 17.5 571.185 0.5 651.185 0.5Z" fill="currentColor"/>
    <path d="M378.757 5.5L331.757 294.5H406.757L453.757 5.5H378.757Z" fill="currentColor"/>
    <path d="M717.757 5.5L638.757 294.5H718.257L742.257 219H830.757L844.257 294.5H917.257L850.257 5.5H717.757ZM766.757 166L789.757 77.5L810.757 166H766.757Z" fill="currentColor"/>
    <path d="M285.757 5.5L187.757 202L178.257 158L148.757 30C143.257 13.5 130.257 6.5 114.757 5.5H0.756958L0.256958 10C0.256958 10 77.257 26 131.257 71.5C182.757 115 207.257 173.5 207.257 173.5L286.757 5.5H285.757Z" fill="currentColor"/>
    <path d="M207.257 173.5L178.257 158C178.257 158 170.257 294.5 285.757 294.5H360.257L378.757 219H304.757C246.257 219 207.257 173.5 207.257 173.5Z" fill="currentColor"/>
  </svg>
);

// Standard Card Component (White/Grey/Blue)
const StandardCard = ({ showDetails, onToggleDetails, cardData }) => {
  return (
    <div className="relative w-full aspect-[1.586/1] rounded-2xl overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02]">
      {/* Card Background - White/Grey/Blue gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-100 to-blue-100">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-blue-200/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gray-300/30 rounded-full blur-2xl"></div>
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #000 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Card Content */}
      <div className="relative h-full p-5 flex flex-col justify-between">
        {/* Top Row - Logo and Type */}
        <div className="flex items-start justify-between">
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Standard</span>
            <div className="text-gray-800 font-bold text-lg tracking-wide">ONERIK</div>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className="w-5 h-5 text-gray-400 rotate-90" />
          </div>
        </div>
        
        {/* Chip */}
        <div className="mt-1">
          <div className="w-11 h-8 rounded-md bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 shadow-md relative overflow-hidden">
            <div className="absolute inset-0 flex flex-col justify-center gap-0.5 px-1">
              {[1,2,3,4].map(i => <div key={i} className="h-0.5 bg-yellow-600/30 rounded"></div>)}
            </div>
          </div>
        </div>
        
        {/* Card Number */}
        <div className="mt-2">
          <div className="font-mono text-lg text-gray-700 tracking-[0.15em] flex items-center gap-2">
            {showDetails ? (
              <span>{cardData?.card_number || '4532 8901 2345 6789'}</span>
            ) : (
              <span>•••• •••• •••• {cardData?.card_number?.slice(-4) || '6789'}</span>
            )}
            <button 
              onClick={onToggleDetails}
              className="p-1 hover:bg-gray-200/50 rounded transition-colors"
            >
              {showDetails ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
            </button>
          </div>
        </div>
        
        {/* Bottom Row */}
        <div className="flex items-end justify-between mt-1">
          <div>
            <div className="text-gray-400 text-[9px] uppercase tracking-wider">Card Holder</div>
            <div className="text-gray-700 font-medium text-sm">{cardData?.cardholder_name || 'JOHN DOE'}</div>
          </div>
          <div>
            <div className="text-gray-400 text-[9px] uppercase tracking-wider">Expires</div>
            <div className="text-gray-700 font-mono text-sm">{showDetails ? (cardData?.expiry || '12/28') : '••/••'}</div>
          </div>
          <div>
            <div className="text-gray-400 text-[9px] uppercase tracking-wider">CVV</div>
            <div className="text-gray-700 font-mono text-sm">{showDetails ? (cardData?.cvv || '123') : '•••'}</div>
          </div>
          <VisaLogo className="w-14 text-blue-600" />
        </div>
      </div>
    </div>
  );
};

// Business Card Component (Dark Blue/Black)
const BusinessCard = ({ showDetails, onToggleDetails, cardData }) => {
  return (
    <div className="relative w-full aspect-[1.586/1] rounded-2xl overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02]">
      {/* Card Background - Dark Blue/Black gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-black">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-800/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-slate-700/20 rounded-full blur-2xl"></div>
        {/* Subtle line pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)`
        }}></div>
      </div>
      
      {/* Card Content */}
      <div className="relative h-full p-5 flex flex-col justify-between">
        {/* Top Row */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Business</span>
            </div>
            <div className="text-white font-bold text-lg tracking-wide">ONERIK</div>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className="w-5 h-5 text-white/60 rotate-90" />
          </div>
        </div>
        
        {/* Chip */}
        <div className="mt-1">
          <div className="w-11 h-8 rounded-md bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 shadow-md relative overflow-hidden">
            <div className="absolute inset-0 flex flex-col justify-center gap-0.5 px-1">
              {[1,2,3,4].map(i => <div key={i} className="h-0.5 bg-yellow-600/30 rounded"></div>)}
            </div>
          </div>
        </div>
        
        {/* Card Number */}
        <div className="mt-2">
          <div className="font-mono text-lg text-white tracking-[0.15em] flex items-center gap-2">
            {showDetails ? (
              <span>{cardData?.card_number || '4532 8901 2345 6789'}</span>
            ) : (
              <span>•••• •••• •••• {cardData?.card_number?.slice(-4) || '6789'}</span>
            )}
            <button 
              onClick={onToggleDetails}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              {showDetails ? <EyeOff className="w-4 h-4 text-white/60" /> : <Eye className="w-4 h-4 text-white/60" />}
            </button>
          </div>
        </div>
        
        {/* Bottom Row */}
        <div className="flex items-end justify-between mt-1">
          <div>
            <div className="text-white/40 text-[9px] uppercase tracking-wider">Card Holder</div>
            <div className="text-white font-medium text-sm">{cardData?.cardholder_name || 'JOHN DOE'}</div>
          </div>
          <div>
            <div className="text-white/40 text-[9px] uppercase tracking-wider">Expires</div>
            <div className="text-white font-mono text-sm">{showDetails ? (cardData?.expiry || '12/28') : '••/••'}</div>
          </div>
          <div>
            <div className="text-white/40 text-[9px] uppercase tracking-wider">CVV</div>
            <div className="text-white font-mono text-sm">{showDetails ? (cardData?.cvv || '123') : '•••'}</div>
          </div>
          <VisaLogo className="w-14 text-white" />
        </div>
      </div>
    </div>
  );
};

// Ultra Metal Card Component (Purple/Gold)
const UltraCard = ({ showDetails, onToggleDetails, cardData }) => {
  return (
    <div className="relative w-full aspect-[1.586/1] rounded-2xl overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02]">
      {/* Card Background - Purple/Gold metallic gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-amber-900">
        {/* Metallic shine effect */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent"></div>
        {/* Gold accent */}
        <div className="absolute top-0 right-0 w-56 h-56 bg-gradient-to-br from-yellow-500/30 to-amber-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/30 rounded-full blur-3xl"></div>
        {/* Brushed metal texture */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)`
        }}></div>
      </div>
      
      {/* Card Content */}
      <div className="relative h-full p-5 flex flex-col justify-between">
        {/* Top Row */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <Crown className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">Ultra Metal</span>
            </div>
            <div className="text-white font-bold text-lg tracking-wide">ONERIK</div>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className="w-5 h-5 text-white/60 rotate-90" />
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          </div>
        </div>
        
        {/* Chip - Gold tinted */}
        <div className="mt-1">
          <div className="w-11 h-8 rounded-md bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 shadow-lg relative overflow-hidden">
            <div className="absolute inset-0 flex flex-col justify-center gap-0.5 px-1">
              {[1,2,3,4].map(i => <div key={i} className="h-0.5 bg-yellow-700/30 rounded"></div>)}
            </div>
          </div>
        </div>
        
        {/* Card Number */}
        <div className="mt-2">
          <div className="font-mono text-lg text-white tracking-[0.15em] flex items-center gap-2">
            {showDetails ? (
              <span>{cardData?.card_number || '4532 8901 2345 6789'}</span>
            ) : (
              <span>•••• •••• •••• {cardData?.card_number?.slice(-4) || '6789'}</span>
            )}
            <button 
              onClick={onToggleDetails}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              {showDetails ? <EyeOff className="w-4 h-4 text-white/60" /> : <Eye className="w-4 h-4 text-white/60" />}
            </button>
          </div>
        </div>
        
        {/* Bottom Row */}
        <div className="flex items-end justify-between mt-1">
          <div>
            <div className="text-yellow-400/60 text-[9px] uppercase tracking-wider">Card Holder</div>
            <div className="text-white font-medium text-sm">{cardData?.cardholder_name || 'JOHN DOE'}</div>
          </div>
          <div>
            <div className="text-yellow-400/60 text-[9px] uppercase tracking-wider">Expires</div>
            <div className="text-white font-mono text-sm">{showDetails ? (cardData?.expiry || '12/28') : '••/••'}</div>
          </div>
          <div>
            <div className="text-yellow-400/60 text-[9px] uppercase tracking-wider">CVV</div>
            <div className="text-white font-mono text-sm">{showDetails ? (cardData?.cvv || '123') : '•••'}</div>
          </div>
          <VisaLogo className="w-14 text-yellow-400" />
        </div>
      </div>
      
      {/* VIP Badge */}
      <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-500/30 to-amber-500/30 border border-yellow-500/40 px-2 py-0.5 rounded-full">
        <span className="text-yellow-400 text-[10px] font-bold uppercase tracking-wider">VIP</span>
      </div>
    </div>
  );
};

// Card Tier Selection Component
const CardTierSelector = ({ selectedTier, onSelectTier, isDark }) => {
  const tiers = [
    {
      id: 'standard',
      name: 'Standard',
      description: 'Perfect for everyday spending',
      features: ['Free virtual card', 'Up to $10,000 limit', '1% cashback', 'Online purchases'],
      price: 'Free',
      icon: CreditCard,
      gradient: 'from-gray-100 to-blue-100',
      textColor: 'text-gray-700'
    },
    {
      id: 'business',
      name: 'Business',
      description: 'For professionals & entrepreneurs',
      features: ['Physical + Virtual cards', 'Up to $50,000 limit', '2% cashback', 'Expense tracking'],
      price: '$9.99/mo',
      icon: Briefcase,
      gradient: 'from-slate-900 to-blue-950',
      textColor: 'text-white'
    },
    {
      id: 'ultra',
      name: 'Ultra Metal',
      description: 'Premium benefits & luxury perks',
      features: ['Metal card', 'Unlimited limit', '3% cashback', 'Lounge access', 'Concierge'],
      price: '$29.99/mo',
      icon: Crown,
      gradient: 'from-purple-900 to-amber-900',
      textColor: 'text-white'
    }
  ];

  const cardBg = isDark ? 'bg-black/40' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {tiers.map((tier) => {
        const Icon = tier.icon;
        const isSelected = selectedTier === tier.id;
        return (
          <button
            key={tier.id}
            onClick={() => onSelectTier(tier.id)}
            className={`relative p-5 rounded-2xl border-2 transition-all text-left ${
              isSelected
                ? 'border-purple-500 ring-2 ring-purple-500/20'
                : `${cardBorder} hover:border-purple-500/50`
            } ${cardBg}`}
          >
            {/* Mini Card Preview */}
            <div className={`w-full h-16 rounded-lg bg-gradient-to-br ${tier.gradient} mb-4 flex items-center justify-center`}>
              <VisaLogo className={`w-10 ${tier.id === 'standard' ? 'text-blue-600' : tier.id === 'ultra' ? 'text-yellow-400' : 'text-white'}`} />
            </div>

            <div className="flex items-center gap-2 mb-2">
              <Icon className={`w-5 h-5 ${tier.id === 'ultra' ? 'text-yellow-500' : tier.id === 'business' ? 'text-blue-500' : 'text-gray-500'}`} />
              <span className={`font-bold ${textPrimary}`}>{tier.name}</span>
            </div>

            <p className={`${textMuted} text-sm mb-3`}>{tier.description}</p>

            <ul className="space-y-1.5 mb-4">
              {tier.features.map((feature, idx) => (
                <li key={idx} className={`text-xs ${textMuted} flex items-center gap-1.5`}>
                  <span className="text-green-500">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <div className={`text-lg font-bold ${tier.id === 'ultra' ? 'text-yellow-500' : tier.id === 'business' ? 'text-blue-500' : textPrimary}`}>
              {tier.price}
            </div>

            {isSelected && (
              <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

const Cards = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState('standard');
  const [cardType, setCardType] = useState('virtual');
  const [eligibleForCard, setEligibleForCard] = useState(false);
  const [previewCard, setPreviewCard] = useState('standard');

  // Theme colors
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const cardBg = isDark ? 'bg-black/40' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const pageBg = isDark ? '' : 'bg-gray-50';

  useEffect(() => {
    fetchCards();
    checkEligibility();
  }, []);

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cards/my-cards?email=user@example.com`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCards(response.data.cards || []);
    } catch (error) {
      console.error('Error fetching cards:', error);
    }
  };

  const checkEligibility = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cards/eligibility?email=user@example.com`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setEligibleForCard(response.data.eligible);
    } catch (error) {
      console.error('Error checking eligibility:', error);
    }
  };

  const requestCard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/cards/issue`, {
        email: 'user@example.com',
        card_type: cardType,
        card_tier: selectedTier,
        client_name: 'John Doe'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCards([...cards, response.data]);
      setShowModal(false);
      toast.success(`${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} ${cardType} card issued successfully!`);
    } catch (error) {
      console.error('Error issuing card:', error);
      toast.error('Failed to issue card');
    } finally {
      setLoading(false);
    }
  };

  const toggleShow = (cardId) => {
    setShowDetails(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
    toast.success(t('common.copied'));
  };

  const renderCardByTier = (tier, showDetailsState, onToggle, cardData = null) => {
    switch (tier) {
      case 'business':
        return <BusinessCard showDetails={showDetailsState} onToggleDetails={onToggle} cardData={cardData} />;
      case 'ultra':
        return <UltraCard showDetails={showDetailsState} onToggleDetails={onToggle} cardData={cardData} />;
      default:
        return <StandardCard showDetails={showDetailsState} onToggleDetails={onToggle} cardData={cardData} />;
    }
  };

  return (
    <div data-testid="cards-page" className={`min-h-screen py-6 px-3 md:px-4 ${pageBg}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary} mb-1`}>{t('cards.myCards')}</h1>
            <p className={`${textMuted} text-sm`}>{t('cards.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2.5 rounded-full transition-all hover:scale-105 active:scale-95"
          >
            <Plus className="w-4 h-4" />
            {t('cards.orderCard')}
          </button>
        </div>

        {/* Card Tier Preview Section */}
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6 mb-6`}>
          <div className="text-center mb-6">
            <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>Choose Your Card</h2>
            <p className={`${textMuted} text-sm`}>Select the card tier that fits your needs</p>
          </div>

          {/* Card Preview Tabs */}
          <div className="flex justify-center gap-2 mb-6">
            {['standard', 'business', 'ultra'].map((tier) => (
              <button
                key={tier}
                onClick={() => setPreviewCard(tier)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  previewCard === tier
                    ? tier === 'ultra' 
                      ? 'bg-gradient-to-r from-purple-600 to-amber-600 text-white'
                      : tier === 'business'
                        ? 'bg-blue-900 text-white'
                        : 'bg-blue-100 text-blue-700'
                    : isDark 
                      ? 'bg-white/10 text-white/60 hover:bg-white/20'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tier === 'ultra' ? 'Ultra Metal' : tier.charAt(0).toUpperCase() + tier.slice(1)}
              </button>
            ))}
          </div>

          {/* Card Preview */}
          <div className="max-w-md mx-auto">
            {renderCardByTier(previewCard, showDetails['preview'], () => toggleShow('preview'))}
          </div>

          <p className={`${textMuted} text-xs text-center mt-4`}>Click the eye icon to reveal card details</p>
        </div>

        {/* Existing Cards */}
        {cards.length > 0 && (
          <div className="mb-6">
            <h3 className={`text-lg font-bold ${textPrimary} mb-4`}>Your Cards</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cards.map((card) => (
                <div key={card.card_id} className="relative">
                  {renderCardByTier(
                    card.card_tier || 'standard',
                    showDetails[card.card_id],
                    () => toggleShow(card.card_id),
                    card
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`text-xs ${textMuted}`}>
                      Balance: <span className="font-mono font-medium">${card.balance?.toFixed(2) || '0.00'}</span>
                    </span>
                    <button
                      onClick={() => copyToClipboard(card.card_number)}
                      className={`text-xs ${textMuted} hover:text-purple-500 flex items-center gap-1`}
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card Tier Comparison */}
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6`}>
          <h3 className={`text-lg font-bold ${textPrimary} mb-4 text-center`}>Compare Card Tiers</h3>
          <CardTierSelector
            selectedTier={selectedTier}
            onSelectTier={setSelectedTier}
            isDark={isDark}
          />
        </div>

        {/* Request Card Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`${isDark ? 'bg-[#111111] border-white/10' : 'bg-white border-gray-200'} border rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto`}>
              <h2 className={`text-xl font-bold ${textPrimary} mb-4`}>Request New Card</h2>
              
              {/* Tier Selection */}
              <div className="mb-4">
                <label className={`${textMuted} text-sm mb-2 block`}>Select Card Tier</label>
                <div className="grid grid-cols-3 gap-2">
                  {['standard', 'business', 'ultra'].map((tier) => (
                    <button
                      key={tier}
                      onClick={() => setSelectedTier(tier)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        selectedTier === tier
                          ? 'border-purple-500 bg-purple-500/10'
                          : isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {tier === 'standard' && <CreditCard className="w-5 h-5 mx-auto mb-1 text-gray-500" />}
                      {tier === 'business' && <Briefcase className="w-5 h-5 mx-auto mb-1 text-blue-500" />}
                      {tier === 'ultra' && <Crown className="w-5 h-5 mx-auto mb-1 text-yellow-500" />}
                      <div className={`text-xs font-medium ${textPrimary} capitalize`}>{tier === 'ultra' ? 'Ultra' : tier}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Card Type Selection */}
              <div className="mb-6">
                <label className={`${textMuted} text-sm mb-2 block`}>Card Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setCardType('virtual')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      cardType === 'virtual'
                        ? 'border-purple-500 bg-purple-500/10'
                        : isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`text-sm font-medium ${textPrimary} mb-0.5`}>Virtual Card</div>
                    <div className={`text-xs ${textMuted}`}>Instant • Online use</div>
                  </button>
                  <button
                    onClick={() => setCardType('physical')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      cardType === 'physical'
                        ? 'border-purple-500 bg-purple-500/10'
                        : isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`text-sm font-medium ${textPrimary} mb-0.5`}>Physical Card</div>
                    <div className={`text-xs ${textMuted}`}>5-7 days • Use anywhere</div>
                  </button>
                </div>
              </div>

              {/* Preview */}
              <div className="mb-6">
                <label className={`${textMuted} text-sm mb-2 block`}>Preview</label>
                <div className="max-w-xs mx-auto">
                  {renderCardByTier(selectedTier, false, () => {})}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowModal(false)}
                  className={`flex-1 ${isDark ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-gray-100 hover:bg-gray-200 border-gray-200'} ${textPrimary} text-sm border font-medium py-3 rounded-xl transition-all`}
                >
                  Cancel
                </button>
                <button
                  onClick={requestCard}
                  disabled={loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-3 rounded-xl transition-all disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    'Request Card'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cards;
